import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  Package,
  Upload,
  Plus,
  MapPin,
  Zap,
} from 'lucide-react';
import DateSelector from './DateSelector';
import DeliveryList from './DeliveryList';
import FinancialSummary from './FinancialSummary';
import StatisticsCards from './StatisticsCards';
import ActionButtons from './ActionButtons';
import EditDeliveryModal from './EditDeliveryModal';
import ImageDepositCarouselModal from './ImageDepositCarouselModal';
import DeliverySearchButton from './DeliverySearchButton';

function Analytics({ userId, setActiveComponent }) {
  const [selectedDate, setSelectedDate] = useState(getTodayISO());
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesProvince, setDeliveriesProvince] = useState([]);
  const [expressDeliveries, setExpressDeliveries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentImages, setPaymentImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  // Keep these because ActionButtons still uses hasActivePickup
  const [hasActivePickup, setHasActivePickup] = useState(false);
  const [checkingPickup, setCheckingPickup] = useState(true);

  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeliveryTypeModal, setShowDeliveryTypeModal] = useState(false);
  const [providerId, setProviderId] = useState(null);

  // New modal-specific states
  const [deliveryTypeError, setDeliveryTypeError] = useState('');
  const [checkingDeliveryType, setCheckingDeliveryType] = useState('');

  const baseUrl = process.env.REACT_APP_API_URL || '';

  function getTodayISO() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Keep this because ActionButtons still depends on it
  useEffect(() => {
    async function checkActivePickup() {
      setCheckingPickup(true);
      try {
        const response = await axios.get(`${baseUrl}/api/check_active_pickup`, {
          params: { user_id: userId },
        });
        setHasActivePickup(response.data.has_active_pickup || false);
      } catch (error) {
        setHasActivePickup(false);
      } finally {
        setCheckingPickup(false);
      }
    }

    if (userId) {
      checkActivePickup();
    } else {
      setCheckingPickup(false);
    }
  }, [userId, baseUrl]);

  useEffect(() => {
    async function fetchProviderId() {
      try {
        const response = await axios.get(`${baseUrl}/api/user/provider_id`, {
          params: { user_id: userId },
        });
        setProviderId(response.data.provider_id ?? null);
      } catch (error) {
        console.error('Error fetching provider_id:', error);
        setProviderId(null);
      }
    }

    if (userId) {
      fetchProviderId();
    }
  }, [userId, baseUrl]);

  useEffect(() => {
    if (selectedDate) {
      fetchDeliveriesForDate(selectedDate);
    }
  }, [selectedDate, userId]);

  async function fetchDeliveriesForDate(date) {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/deliveries_by_date`, {
        params: {
          user_id: userId,
          date: date,
        },
      });

      setDeliveries(response.data.deliveries || []);
      setDeliveriesProvince(response.data.deliveries_provincia || []);
      setExpressDeliveries(response.data.express_deliveries || []);
      setOrders(response.data.orders || []);
      setPaymentImages(response.data.payment_images || []);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  async function checkActivePickupNow() {
    try {
      const response = await axios.get(`${baseUrl}/api/check_active_pickup`, {
        params: { user_id: userId },
      });

      return {
        ok: !!response.data.has_active_pickup,
        data: response.data,
      };
    } catch (error) {
      return {
        ok: false,
        data: null,
      };
    }
  }

  function canEdit(delivery) {
    if (delivery.status !== 'pending') {
      return false;
    }

    let deliveryDateOnly = delivery.delivery_date;

    if (deliveryDateOnly.includes('T')) {
      deliveryDateOnly = deliveryDateOnly.split('T')[0];
    } else if (deliveryDateOnly.includes(' ')) {
      deliveryDateOnly = deliveryDateOnly.split(' ')[0];
    }

    const now = new Date();
    const peruDateFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const peruHourFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Lima',
      hour: 'numeric',
      hour12: false,
    });

    const todayPeru = peruDateFormatter.format(now);
    const currentHourPeru = parseInt(peruHourFormatter.format(now), 10);

    if (deliveryDateOnly < todayPeru) {
      return false;
    }

    if (deliveryDateOnly === todayPeru) {
      const canEditToday = currentHourPeru < 12;
      console.log(`Result: TODAY - hour ${currentHourPeru} < 12? ${canEditToday}`);
      return canEditToday;
    }

    if (deliveryDateOnly >= todayPeru) {
      console.log('Result: FUTURE - can edit');
      return true;
    }

    return false;
  }

  function calculateFinancials() {
    const isProvider = providerId !== null;
    let totalCollected = 0;
    let totalExchange = 0;
    let totalServiceFee = 0;
    let deliveryCount = 0;
    let deliveryCountProvince = 0;
    let regularPriceDeliveries = 0;
    let dropshipDeliveries = 0;
    let dropshipTotalCost = 0;
    let totalProvinciaCosts = 0;

    let limaCollected = 0;
    let limaServiceFee = 0;
    let limaDropshipCost = 0;

    let provinciaCollected = 0;
    let provinciaServiceFee = 0;
    let provinciaTransportCost = 0;
    let provinciaDropshipCost = 0;

    deliveries.forEach((delivery) => {
      if (delivery.status === 'entregado' || delivery.status === 'rechazado') {
        deliveryCount++;

        if (delivery.charged) {
          const chargedAmount = parseFloat(delivery.charged) || 0;
          totalCollected += chargedAmount;
          limaCollected += chargedAmount;
        }

        if (delivery.fastmax_transfer_amount) {
          const transferAmount = parseFloat(delivery.fastmax_transfer_amount) || 0;
          totalCollected += transferAmount;
          limaCollected += transferAmount;
        }

        if (delivery.fastmax_exchange_amount) {
          const exchangeAmount = parseFloat(delivery.fastmax_exchange_amount) || 0;
          totalExchange += exchangeAmount;
        }

        const isDropship =
          delivery.provider_id !== null && delivery.provider_id !== undefined;

        if (isDropship) {
          dropshipDeliveries++;
          const packageItems = delivery.package_items || [];
          packageItems.forEach((item) => {
            const itemDropshipCost =
              (item.dropship_price || 0) * (item.quantity || 1);
            dropshipTotalCost += itemDropshipCost;
            limaDropshipCost += itemDropshipCost;
          });
        }

        if (delivery.quince_soles) {
          totalServiceFee += 15;
          limaServiceFee += 15;
        } else if (delivery.veinte_soles) {
          totalServiceFee += 20;
          limaServiceFee += 20;
        } else {
          if (isProvider) {
            totalServiceFee += 7.9;
            limaServiceFee += 7.9;
          } else {
            regularPriceDeliveries++;
          }
        }
      }
    });

    deliveriesProvince.forEach((delivery) => {
      if (delivery.status === 'entregado' || delivery.status === 'rechazado') {
        deliveryCountProvince++;

        const charged = parseFloat(delivery.charged) || 0;
        const depositAdvance = parseFloat(delivery.deposit_advance) || 0;
        const transportPrice = parseFloat(delivery.transport_price) || 0;

        const serviceFee = 4;
        totalServiceFee += serviceFee;
        provinciaServiceFee += serviceFee;

        totalProvinciaCosts += transportPrice;
        provinciaTransportCost += transportPrice;

        const isDropship =
          delivery.provider_id !== null && delivery.provider_id !== undefined;

        if (isDropship) {
          dropshipDeliveries++;
          const packageItems = delivery.package_items || [];
          packageItems.forEach((item) => {
            const itemDropshipCost =
              (item.dropship_price || 0) * (item.quantity || 1);
            dropshipTotalCost += itemDropshipCost;
            provinciaDropshipCost += itemDropshipCost;
          });
        }

        const collectedAmount = charged + depositAdvance;
        totalCollected += collectedAmount;
        provinciaCollected += collectedAmount;
      }
    });

    if (!isProvider && regularPriceDeliveries > 0) {
      let regularFee = 0;
      if (regularPriceDeliveries === 1 && deliveryCount === 1) {
        regularFee = 15;
      } else {
        regularFee = regularPriceDeliveries * 9.9;
      }
      totalServiceFee += regularFee;
      limaServiceFee += regularFee;
    }

    limaCollected = limaCollected - totalExchange;
    totalCollected = totalCollected - totalExchange;

    const limaToDeposit = limaCollected - limaServiceFee - limaDropshipCost;
    const provinciaToDeposit =
      provinciaCollected -
      provinciaServiceFee -
      provinciaTransportCost -
      provinciaDropshipCost;
    const totalToDeposit =
      totalCollected -
      totalServiceFee -
      totalProvinciaCosts -
      dropshipTotalCost;

    const totalDeliveryCount = deliveryCount + deliveryCountProvince;

    const pricePerDelivery =
      totalDeliveryCount > 0
        ? (totalServiceFee + totalProvinciaCosts) / totalDeliveryCount
        : 0;

    return {
      totalCollected,
      totalToDeposit,
      deliveryCount,
      deliveryCountProvince,
      totalDeliveryCount,
      pricePerDelivery,
      totalServiceFee,
      totalExchange,
      totalProvinciaCosts,
      dropshipDeliveries,
      dropshipTotalCost,
      limaCollected,
      limaServiceFee,
      limaDropshipCost,
      limaToDeposit,
      provinciaCollected,
      provinciaServiceFee,
      provinciaTransportCost,
      provinciaDropshipCost,
      provinciaToDeposit,
    };
  }

  async function handleUpdateDelivery(deliveryId, updatedData) {
    try {
      const response = await axios.put(`${baseUrl}/api/delivery_points/${deliveryId}`, {
        user_id: userId,
        ...updatedData,
      });

      await fetchDeliveriesForDate(selectedDate);
      setEditingDelivery(null);

      return response.data;
    } catch (error) {
      console.error('Error updating delivery:', error);
      throw error;
    }
  }

  async function handleDeleteDelivery(deliveryId) {
    console.log('Deleting delivery:', deliveryId);

    try {
      const response = await axios.delete(`${baseUrl}/api/delivery_points/${deliveryId}`);

      console.log('DELETE SUCCESS:', response.data);

      await fetchDeliveriesForDate(selectedDate);
      setEditingDelivery(null);
    } catch (error) {
      console.error('DELETE ERROR:', error.response?.data || error.message);
      throw error;
    }
  }

  function handleEntregaClick() {
    setDeliveryTypeError('');
    setShowDeliveryTypeModal(true);
  }

  async function handleSelectLima() {
    setDeliveryTypeError('');
    setCheckingDeliveryType('lima');

    const result = await checkActivePickupNow();

    setCheckingDeliveryType('');

    if (!result.ok) {
      setDeliveryTypeError(
        'Primero debes registrar un recojo activo para agregar una entrega de Lima.'
      );
      return;
    }

    setShowDeliveryTypeModal(false);
    setActiveComponent('Entrega');
  }

  async function handleSelectProvincia() {
    setDeliveryTypeError('');
    setCheckingDeliveryType('provincia');

    const result = await checkActivePickupNow();

    setCheckingDeliveryType('');

    if (!result.ok) {
      setDeliveryTypeError(
        'Primero debes registrar un recojo activo para agregar una entrega de provincia.'
      );
      return;
    }

    setShowDeliveryTypeModal(false);
    setActiveComponent('EntregaProvincia');
  }

  function handleSelectExpress() {
    setDeliveryTypeError('');
    setShowDeliveryTypeModal(false);
    setActiveComponent('EntregaExpress');
  }

  const financials = calculateFinancials();
  const modalButtonsDisabled =
    checkingDeliveryType === 'lima' || checkingDeliveryType === 'provincia';

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Options')}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden text-sm sm:inline sm:text-base">Volver</span>
            </button>

            <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
              Analítica
            </h1>

            <div className="flex items-center gap-0 sm:gap-2">
              {/* Historical Delivery Search */}
              <DeliverySearchButton />

              {/* Entrega Button */}
              <button
                onClick={handleEntregaClick}
                className="p-2 rounded-lg transition-colors group relative text-[#B1336E] hover:bg-[#B1336E]/10 cursor-pointer"
              >
                <Plus className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50 shadow-lg">
                  Agregar Entrega
                </span>
              </button>

              {/* Recojo Button */}
              <button
                onClick={() => setActiveComponent('Recojo')}
                className="p-2 text-[#E6B43C] hover:bg-[#E6B43C]/10 rounded-lg transition-colors group relative"
              >
                <MapPin className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                  Agregar Recojo
                </span>
              </button>

              {/* Masivo Button */}
              <button
                onClick={() => setActiveComponent('MasivoWizard')}
                className="p-2 text-[#67A43C] hover:bg-[#67A43C]/10 rounded-lg transition-colors group relative"
              >
                <Upload className="w-5 h-5" />
                <span className="absolute top-full right-0 mt-2 px-2 py-1 bg-slate-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none font-montserrat z-50">
                  Carga Masiva
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            showCalendar={showCalendar}
            onToggleCalendar={() => setShowCalendar(!showCalendar)}
          />

          <StatisticsCards
            deliveries={deliveries}
            deliveriesProvince={deliveriesProvince}
            orders={orders}
            selectedDate={selectedDate}
            expressDeliveries={expressDeliveries}
            userId={userId}
          />

          <FinancialSummary
            financials={financials}
            paymentImages={paymentImages}
            onShowImages={() => setShowImageModal(true)}
            expressDeliveries={expressDeliveries}
          />

          {deliveries.length === 0 &&
          deliveriesProvince.length === 0 &&
          orders.length === 0 &&
          expressDeliveries.length === 0 ? (
            <ActionButtons
              selectedDate={selectedDate}
              setActiveComponent={setActiveComponent}
              hasActivePickup={hasActivePickup}
            />
          ) : (
            <DeliveryList
              deliveries={deliveries}
              deliveriesProvince={deliveriesProvince}
              orders={orders}
              canEdit={canEdit}
              onEdit={setEditingDelivery}
              loading={loading}
              selectedDate={selectedDate}
              setActiveComponent={setActiveComponent}
              expressDeliveries={expressDeliveries}
              userId={userId}
            />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingDelivery && (
        <EditDeliveryModal
          delivery={editingDelivery}
          onClose={() => setEditingDelivery(null)}
          onSave={handleUpdateDelivery}
          onDelete={handleDeleteDelivery}
        />
      )}

      {/* Image Carousel Modal */}
      {showImageModal && (
        <ImageDepositCarouselModal
          images={paymentImages}
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
        />
      )}

      {/* Delivery Type Modal */}
      {showDeliveryTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900 font-montserrat mb-2">
                Selecciona el Tipo de Entrega
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                ¿Dónde se realizará la entrega?
              </p>
            </div>

            <div className="space-y-3">
              {/* Lima Option */}
              <button
                onClick={handleSelectLima}
                disabled={modalButtonsDisabled}
                className="w-full bg-brand-primary hover:bg-brand-primary/90 disabled:opacity-60 text-white py-4 px-6 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base">
                      {checkingDeliveryType === 'lima'
                        ? 'Verificando...'
                        : 'Lima Metropolitana'}
                    </div>
                    <div className="text-xs text-white/80 font-normal">
                      Entrega local con motorizado
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>

              {/* Provincia Option */}
              <button
                onClick={handleSelectProvincia}
                disabled={modalButtonsDisabled}
                className="w-full bg-brand-gold hover:bg-brand-gold/90 disabled:opacity-60 text-white py-4 px-6 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base">
                      {checkingDeliveryType === 'provincia'
                        ? 'Verificando...'
                        : 'Provincia'}
                    </div>
                    <div className="text-xs text-white/80 font-normal">
                      Envío por agencia de transporte
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>

              {/* Express Option */}
              <button
                onClick={handleSelectExpress}
                disabled={modalButtonsDisabled}
                className="w-full bg-brand-green hover:bg-brand-green/90 disabled:opacity-60 text-white py-4 px-6 rounded-lg transition-all font-montserrat font-semibold flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="text-left">
                    <div className="text-base">Express</div>
                    <div className="text-xs text-white/80 font-normal">
                      Delivery por km · Solo Lima
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>

            {deliveryTypeError && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 font-montserrat">
                {deliveryTypeError}
              </div>
            )}

            <button
              onClick={() => {
                setDeliveryTypeError('');
                setShowDeliveryTypeModal(false);
              }}
              className="w-full mt-4 bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 rounded-lg transition-colors font-montserrat font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
